import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/**
 * LumberLinq theme preset: Aura base with the brand TEAL as the primary colour (changed from indigo on 2026-10-08, theme plan
 * docs/THEME_PLAN_2026-10-07.md). Replaces the old 10-theme CSS-file switcher (removed in PrimeNG v18+).
 *
 * Phase 1 (buttons and actions): three button looks only.
 *   Primary   = the teal gradient of the header "+ New" button. Used for success, help, warn, contrast and the default.
 *   Secondary = calm grey, no gradient. Used for the old "info" kind too.
 *   Danger    = ruby gradient (deletes only).
 * Status colours stay where colour carries meaning (tags, badges, toasts, messages): they are NOT touched here.
 * Gradients, glows and motion are in src/ll-theme.css (tokens cannot hold them).
 * SIZES are not touched anywhere in this file (owner rule: tally grid sizes are never changed; buttons keep today's padding and height).
 */

const PRIMARY_GRADIENT = 'linear-gradient(135deg, #1684ac, #1fa39a)';
const PRIMARY_GRADIENT_HOVER = 'linear-gradient(135deg, #127396, #1a9189)';
const PRIMARY_GRADIENT_ACTIVE = 'linear-gradient(135deg, #0e5f7c, #16807a)';
const DANGER_GRADIENT = 'linear-gradient(135deg, #b91c1c, #ef4444)';
const DANGER_GRADIENT_HOVER = 'linear-gradient(135deg, #a31919, #e23838)';
const DANGER_GRADIENT_ACTIVE = 'linear-gradient(135deg, #8c1515, #cf2f2f)';

/** A filled button look. `bg` may be a gradient (PrimeNG applies it with `background:`). */
const filled = (bg: string, hover: string, active: string, color: string, ring: string) => ({
  background: bg,
  hoverBackground: hover,
  activeBackground: active,
  borderColor: 'transparent',
  hoverBorderColor: 'transparent',
  activeBorderColor: 'transparent',
  color,
  hoverColor: color,
  activeColor: color,
  focusRing: { color: ring, shadow: 'none' },
});

const primaryFilled = filled(PRIMARY_GRADIENT, PRIMARY_GRADIENT_HOVER, PRIMARY_GRADIENT_ACTIVE, '#ffffff', '#1684ac');
const dangerFilled = filled(DANGER_GRADIENT, DANGER_GRADIENT_HOVER, DANGER_GRADIENT_ACTIVE, '#ffffff', '#ef4444');

const secondaryFilled = (dark: boolean) =>
  dark
    ? { background: '#1b2a38', hoverBackground: '#223648', activeBackground: '#2a4258', borderColor: '#2c4052', hoverBorderColor: '#35526a', activeBorderColor: '#3f6280', color: '#e6eef5', hoverColor: '#ffffff', activeColor: '#ffffff', focusRing: { color: '#93a8b6', shadow: 'none' } }
    : { background: '#eef2f6', hoverBackground: '#e4eaf0', activeBackground: '#d9e1e8', borderColor: '#d3dce5', hoverBorderColor: '#c3d0da', activeBorderColor: '#b4c3cf', color: '#1f2937', hoverColor: '#111827', activeColor: '#111827', focusRing: { color: '#566b7a', shadow: 'none' } };

/** Outlined and text looks: one colour, soft tint on hover and press. */
const tint = (c: string, hover: string, active: string) => ({ color: c, hoverBackground: hover, activeBackground: active });
const outlined = (c: string, border: string, hover: string, active: string) => ({ color: c, borderColor: border, hoverBackground: hover, activeBackground: active });

const variants = (dark: boolean) => {
  const teal = dark ? '#5cc8e6' : '#0e6f90';
  const tealBorder = dark ? 'rgba(92, 200, 230, 0.55)' : 'rgba(14, 111, 144, 0.55)';
  const tealHover = dark ? 'rgba(53, 179, 216, 0.16)' : 'rgba(22, 132, 172, 0.10)';
  const tealActive = dark ? 'rgba(53, 179, 216, 0.26)' : 'rgba(22, 132, 172, 0.18)';
  const red = dark ? '#fca5a5' : '#b91c1c';
  const redBorder = dark ? 'rgba(252, 165, 165, 0.5)' : 'rgba(185, 28, 28, 0.5)';
  const redHover = dark ? 'rgba(239, 68, 68, 0.18)' : 'rgba(185, 28, 28, 0.08)';
  const redActive = dark ? 'rgba(239, 68, 68, 0.28)' : 'rgba(185, 28, 28, 0.16)';
  const grey = dark ? '#e6eef5' : '#1f2937';
  const greyBorder = dark ? '#35526a' : '#c3d0da';
  const greyHover = dark ? 'rgba(255, 255, 255, 0.07)' : 'rgba(15, 34, 48, 0.06)';
  const greyActive = dark ? 'rgba(255, 255, 255, 0.13)' : 'rgba(15, 34, 48, 0.12)';

  const outlinedTeal = outlined(teal, tealBorder, tealHover, tealActive);
  const outlinedGrey = outlined(grey, greyBorder, greyHover, greyActive);
  const textTeal = tint(teal, tealHover, tealActive);
  const textGrey = tint(grey, greyHover, greyActive);
  return {
    outlined: {
      primary: outlinedTeal, success: outlinedTeal, help: outlinedTeal, warn: outlinedTeal, contrast: outlinedTeal,
      secondary: outlinedGrey, info: outlinedGrey, plain: outlinedGrey,
      danger: outlined(red, redBorder, redHover, redActive),
    },
    text: {
      primary: textTeal, success: textTeal, help: textTeal, warn: textTeal, contrast: textTeal,
      secondary: textGrey, info: textGrey, plain: textGrey,
      danger: tint(red, redHover, redActive),
    },
  };
};

const scheme = (dark: boolean) => {
  const sec = secondaryFilled(dark);
  const v = variants(dark);
  return {
    root: {
      primary: primaryFilled, success: primaryFilled, help: primaryFilled, warn: primaryFilled, contrast: primaryFilled,
      secondary: sec, info: sec,
      danger: dangerFilled,
    },
    outlined: v.outlined,
    text: v.text,
  };
};

/** Phase 2: checked checkbox, radio button and switch use the same teal gradient as the primary button. */
const checkedLook = {
  checkedBackground: PRIMARY_GRADIENT,
  checkedHoverBackground: PRIMARY_GRADIENT_HOVER,
  checkedBorderColor: 'transparent',
  checkedHoverBorderColor: 'transparent',
  checkedFocusBorderColor: '#1684ac',
};

export const LumberLinqPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#eaf6fa',
      100: '#cfe9f2',
      200: '#a2d3e5',
      300: '#6fb9d4',
      400: '#3d9dc1',
      500: '#1684ac',
      600: '#127396',
      700: '#0e5f7c',
      800: '#0b4a61',
      900: '#083a4c',
      950: '#052733',
    },
    // Phase 2 (form fields): 10px rounded corners, teal focus ring and hover border. Padding, height and font size are NOT changed.
    formField: {
      borderRadius: '10px',
      focusRing: { width: '3px', style: 'solid', color: 'rgba(22, 132, 172, 0.35)', offset: '0', shadow: 'none' },
    },
    overlay: {
      select: { borderRadius: '12px' },
      popover: { borderRadius: '12px' },
      modal: { borderRadius: '16px' },
    },
    colorScheme: {
      light: { formField: { hoverBorderColor: '{primary.300}' } },
      dark: { formField: { hoverBorderColor: '{primary.500}' } },
    },
  },
  components: {
    // Phase 4: the old PrimeNG spinner turns in brand teal shades instead of red / blue / green / yellow.
    progressspinner: {
      colorScheme: {
        light: { root: { colorOne: '#1684ac', colorTwo: '#1fa39a', colorThree: '#1684ac', colorFour: '#1fa39a' } },
        dark: { root: { colorOne: '#35b3d8', colorTwo: '#26b5aa', colorThree: '#35b3d8', colorFour: '#26b5aa' } },
      },
    },
    // Phase 3 (tables, cards, tabs): shape and highlight colours only; no paddings, heights or font sizes.
    card: { root: { borderRadius: '14px' } },
    tabs: { activeBar: { background: PRIMARY_GRADIENT } },
    paginator: {
      navButton: { borderRadius: '999px' },
      colorScheme: {
        light: { navButton: { selectedBackground: PRIMARY_GRADIENT, selectedColor: '#ffffff' } },
        dark: { navButton: { selectedBackground: PRIMARY_GRADIENT, selectedColor: '#ffffff' } },
      },
    },
    checkbox: { colorScheme: { light: { root: checkedLook }, dark: { root: checkedLook } } },
    radiobutton: { colorScheme: { light: { root: checkedLook }, dark: { root: checkedLook } } },
    toggleswitch: {
      colorScheme: {
        light: { root: { checkedBackground: PRIMARY_GRADIENT, checkedHoverBackground: PRIMARY_GRADIENT_HOVER } },
        dark: { root: { checkedBackground: PRIMARY_GRADIENT, checkedHoverBackground: PRIMARY_GRADIENT_HOVER } },
      },
    },
    button: {
      // Pill shape for buttons (the header "+ New" look). Padding, height and font size are NOT changed.
      root: {
        borderRadius: '999px',
        label: { fontWeight: '600' },
      },
      colorScheme: {
        light: scheme(false),
        dark: scheme(true),
      },
    },
  },
});
